import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';

export default function AboutPage() {
  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于我们 - 纲手本子·高质量同人创作合集',
          description: '了解纲手本子平台的品牌故事、口碑评价与联系方式，致力于为火影忍者纲手角色粉丝提供最优质的同人创作内容。',
          url: 'https://tsunade-doujin.readdy.ai/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8">关于我们</h1>

            {/* Brand Introduction */}
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">品牌介绍</h2>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 space-y-4">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  纲手本子·高质量同人创作合集是一个专注于火影忍者纲手角色同人创作的内容聚合平台。我们致力于汇聚全网最优质的纲手同人插画、漫画与同人故事，为每一位热爱纲手姬的资深火影粉丝打造一站式的深度内容体验。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  纲手作为木叶村的五代目火影、传说中的三忍之一，以其强大的医疗忍术、惊人的怪力以及感人至深的人物故事，在火影忍者粉丝心中有着不可替代的地位。我们深知纲手角色的魅力所在，因此倾力打造了这个专门面向纲手粉丝的同人创作平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们的使命是——<strong>让每一份对纲手的热爱，都能在这里找到归属</strong>。无论你是喜欢欣赏精美的纲手插画，还是热衷于阅读引人入胜的同人漫画与小说，这里都有你想要的精彩内容。
                </p>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: '木叶の风', text: '终于找到一个专门收录纲手同人的平台了！内容质量很高，画风精美，每周都来看更新。', rating: 5 },
                  { name: '樱满开', text: '作为纲手大人的死忠粉，这个网站简直就是宝藏。分类清晰，漫画故事都很精彩！', rating: 5 },
                  { name: '忍道传承', text: '特别喜欢同人故事板块，文笔好得让人感动。纲手师傅和小樱的羁绊故事看哭了。', rating: 5 },
                  { name: '火影老粉', text: '界面很清爽，浏览体验很好。推荐的插画质量都超棒，已经收藏了十几张做壁纸。', rating: 5 },
                  { name: '赌场常客', text: '笑死，那个纲手赌场四格漫画太搞笑了，画出精髓了！更新频率也很稳定。', rating: 5 },
                  { name: '暗部观察员', text: '内容覆盖很全面，插画漫画故事都有。希望以后能增加画师访谈和创作教程。', rating: 4 },
                ].map((review, i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <i
                          key={j}
                          className={`ri-star-fill text-sm ${j < review.rating ? 'text-primary-500' : 'text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">—— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Stats */}
            <section className="mb-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '9+', label: '精选作品' },
                  { value: '3', label: '内容分类' },
                  { value: '50K+', label: '月访问量' },
                  { value: '15K+', label: '粉丝收藏' },
                ].map((stat, i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                    <div className="font-heading text-2xl md:text-3xl text-accent-600 mb-1">{stat.value}</div>
                    <p className="text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 space-y-4">
                  <h3 className="font-heading text-base text-foreground-900">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-700">
                    <p className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-600"></i>
                      contact@tsunade-doujin.readdy.ai
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-weibo-line text-accent-600"></i>
                      @纲手本子同人站
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-bilibili-line text-accent-600"></i>
                      纲手本子创作站
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-wechat-line text-accent-600"></i>
                      纲手本子官方号
                    </p>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    如需投稿、合作或有任何问题建议，欢迎通过以上方式联系我们。我们通常会在24小时内回复。
                  </p>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="font-heading text-base text-foreground-900 mb-4">留言反馈</h3>
                  <ContactForm />
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setStatus({ type: 'success', text: '留言已提交，我们会尽快回复！' });
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') body.append(key, value as string);
      });

      const res = await fetch('https://readdy.ai/api/form/d9q4t46e3oq4jqumtge0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus({ type: 'success', text: '留言已提交，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setStatus({ type: 'error', text: serverMsg });
      }
    } catch {
      setStatus({ type: 'error', text: '网络错误，请稍后重试' });
    }
  };

  return (
    <form id="contact-form" onSubmit={handleSubmit} data-readdy-form="">
      <div className="space-y-3">
        <input
          type="text"
          name="name"
          placeholder="你的昵称"
          required
          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
        />
        <input
          type="email"
          name="email"
          placeholder="你的邮箱"
          required
          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
        />
        <textarea
          name="message"
          placeholder="写下你想说的话..."
          rows={3}
          maxLength={500}
          required
          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
        ></textarea>
        <input
          type="text"
          name="phone_alt"
          className="honeypot-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          className="w-full px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          提交留言
        </button>
      </div>
      {status && (
        <p className={`text-sm mt-3 ${status.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
          {status.text}
        </p>
      )}
    </form>
  );
}